import { useEffect, useMemo, useRef } from 'react';
import { Button, Stack, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { TransactionExportFormState } from '@Forms/transactions/TransactionExportFormContext';
import { useTransactionExportsTable } from '@Modal/hooks/transactionExports/useTransactionExportsTable';

type Props = {
  defaultStartDate?: string;
  defaultEndDate?: string;
};

const formatToLocalDateTime = (isoString: string) => {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const TransactionExportContent: React.FC<Props> = ({ defaultStartDate, defaultEndDate }) => {
  const { control, setValue, watch, trigger, formState } =
    useFormContext<TransactionExportFormState>();
  const { closeTransactionExportModal, handleExportTransactionsToExcel } =
    useTransactionExportsTable();
  const isInitialized = useRef(false);

  const watchedValues = watch();
  const startDate = watchedValues.startDate || '';
  const endDate = watchedValues.endDate || '';

  const { errors } = formState;

  useEffect(() => {
    if (isInitialized.current && endDate) {
      trigger('endDate');
    }
  }, [startDate, endDate, trigger]);

  useEffect(() => {
    if (defaultStartDate) {
      const formattedDate = formatToLocalDateTime(defaultStartDate);
      setValue('startDate', formattedDate, { shouldValidate: true, shouldDirty: true });
    }
    if (defaultEndDate) {
      const formattedDate = formatToLocalDateTime(defaultEndDate);
      setValue('endDate', formattedDate, { shouldValidate: true, shouldDirty: true });
    }
    isInitialized.current = true;
  }, [setValue]);

  const minDate = useMemo(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    return formatToLocalDateTime(thirtyDaysAgo.toISOString());
  }, []);

  const maxDate = useMemo(() => {
    return formatToLocalDateTime(new Date().toISOString());
  }, []);

  const isExportEnabled = useMemo(() => {
    const hasValues = !!startDate && !!endDate;
    const hasNoErrors = !errors.startDate && !errors.endDate;
    return hasValues && hasNoErrors;
  }, [errors.startDate, errors.endDate, startDate, endDate]);

  return (
    <Stack sx={{ width: '100%', padding: '16px', gap: '16px' }}>
      <Controller
        name="startDate"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            size="small"
            label="Start Date & Time"
            type="datetime-local"
            data-at-text-field="StartDateFilter"
            fullWidth
            error={!!error}
            helperText={error?.message}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: minDate,
              max: maxDate,
            }}
          />
        )}
      />
      <Controller
        name="endDate"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            size="small"
            label="End Date & Time"
            type="datetime-local"
            data-at-text-field="EndDateFilter"
            fullWidth
            error={!!error}
            helperText={error?.message}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: minDate,
              max: maxDate,
            }}
          />
        )}
      />
      <Stack sx={{ flexDirection: 'row', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
        <Button
          size="small"
          variant="contained"
          data-at-button="Export"
          onClick={() => handleExportTransactionsToExcel(startDate, endDate)}
          disabled={!isExportEnabled}
        >
          Export
        </Button>
        <Button
          size="small"
          variant="text"
          color="inherit"
          data-at-button="Cancel"
          onClick={closeTransactionExportModal}
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};
